/**
 * Element Type Definitions for MinderBox Collections
 * 
 * This file defines the type system for heterogeneous elements in collections.
 * Uses JSDoc for type documentation since the project uses JavaScript.
 */

import { generateId } from '../utils/Utils';

/**
 * @typedef {'task' | 'note' | 'habit' | 'timer' | 'stopwatch' | 'timeTracker'} ElementType
 */

/**
 * @typedef {Object} BaseElement
 * @property {string} id - Unique identifier for the element
 * @property {ElementType} type - The type of element
 */

/**
 * @typedef {BaseElement & { type: 'task', title: string, completed: boolean, subtasks?: TaskElement[] }} TaskElement
 */

/**
 * @typedef {BaseElement & { type: 'note', title: string, content: string, lastUpdated: string }} NoteElement
 */

/**
 * @typedef {BaseElement & { type: 'habit', note?: string, count?: number, unit?: string, frequency: 'daily' | 'weekly' | 'monthly', trackedDays?: number[], periodTargetDays?: number, completedDates: string[], progressByDate?: Record<string, number>, smartHabit?: { enabled: boolean, goal?: object, longTermGoal?: string, steps: Array<string | object>, currentStepIndex: number, currentStepStartedAt?: string, promptThreshold?: number, hardStepPromptDismissedAt?: string } }} HabitElement
 */


/**
 * @typedef {BaseElement & { type: 'timer', title: string, subtimers: Array<{id: string, text: string, targetTime: number, timeHistory: number[]}>, completedDates: string[] }} TimerElement
 */

/**
 * @typedef {BaseElement & { type: 'stopwatch', title: string, steps: Array<{id: string, text: string, timeHistory: number[], completed: boolean}>, completedDates: string[], sessionHistory: Array<object> }} StopwatchElement
 */

/**
 * @typedef {BaseElement & { type: 'timeTracker', title: string, activities: Array<object>, segments: Array<object> }} TimeTrackerElement
 */

/**
 * @typedef {TaskElement | NoteElement | HabitElement | TimerElement | StopwatchElement | TimeTrackerElement} Element
 */

/**
 * @typedef {Object} Collection
 * @property {string} id - Unique identifier for the collection
 * @property {string} name - Display name of the collection
 * @property {Element[]} elements - Array of heterogeneous elements
 */

/**
 * @typedef {Object} Folder
 * @property {string} id - Unique identifier for the folder
 * @property {string} name - Display name of the folder
 * @property {Collection[]} collections - Array of collections in this folder
 * @property {boolean} [collapsed] - Whether the folder is collapsed in UI
 */

/**
 * Legacy task structure for migration
 * @typedef {Object} LegacyTask
 * @property {string} id
 * @property {string} task
 * @property {boolean} completed
 * @property {boolean} edit
 * @property {LegacyTask[]} [subtasks]
 */

/**
 * Legacy list structure for migration
 * @typedef {Object} LegacyList
 * @property {string} id
 * @property {string} name
 * @property {LegacyTask[]} todos
 */

/**
 * Converts a legacy task to a TaskElement
 * @param {LegacyTask} legacyTask - The legacy task to convert
 * @returns {TaskElement}
 */
export const convertLegacyTaskToElement = (legacyTask) => {
    const element = {
        id: legacyTask.id,
        type: 'task',
        title: legacyTask.task,
        completed: legacyTask.completed,
    };

    if (legacyTask.subtasks && legacyTask.subtasks.length > 0) {
        element.subtasks = legacyTask.subtasks.map(subtask => 
            convertLegacyTaskToElement(subtask)
        );
    }

    return element;
};

/**
 * Converts a legacy list to a Collection
 * @param {LegacyList} legacyList - The legacy list to convert
 * @returns {Collection}
 */
export const convertLegacyListToCollection = (legacyList) => {
    return {
        id: legacyList.id,
        name: legacyList.name,
        elements: (legacyList.todos || []).map(task => 
            convertLegacyTaskToElement(task)
        ),
    };
};

/**
 * Creates a new TaskElement
 * @param {string} title - The task title
 * @param {string} [id] - Optional custom ID
 * @returns {TaskElement}
 */
export const createTaskElement = (title, id) => {
    return {
        id: id || generateId(),
        type: 'task',
        title,
        completed: false,
    };
};

/**
 * Creates a new NoteElement
 * @param {string} title - The note title
 * @param {string} content - The note content
 * @param {string} [id] - Optional custom ID
 * @returns {NoteElement}
 */
export const createNoteElement = (title, content, id) => {
    return {
        id: id || generateId(),
        type: 'note',
        title,
        content,
        lastUpdated: new Date().toISOString(),
    };
};

/**
 * Creates a new HabitElement
 * @param {'daily' | 'weekly' | 'monthly'} frequency - The habit frequency
 * @param {string} [id] - Optional custom ID
 * @returns {HabitElement}
 */
export const createHabitElement = (frequency, id) => {
    return {
        id: id || generateId(),
        type: 'habit',
        frequency,
        completedDates: [],
    };
};


/**
 * Creates a new TimerElement
 * @param {string} title - The timer title
 * @param {Array<{id: string, text: string, targetTime: number, timeHistory: number[]}>} [subtimers] - Optional subtimers array
 * @param {string} [id] - Optional custom ID
 * @returns {TimerElement}
 */
export const createTimerElement = (title, subtimers, id) => {
    return {
        id: id || generateId(),
        type: 'timer',
        title,
        subtimers: subtimers || [],
        completedDates: [],
    };
};

/**
 * Creates a new StopwatchElement
 * @param {string} title - The stopwatch title
 * @param {Array<{id: string, text: string, timeHistory: number[], completed: boolean}>} [steps]
 * @param {string} [id] - Optional custom ID
 * @returns {StopwatchElement}
 */
export const createStopwatchElement = (title, steps, id) => ({
    id: id || generateId(),
    type: 'stopwatch',
    title,
    steps: steps || [],
    completedDates: [],
    sessionHistory: [],
});
